import { updateNoTypeRectangles } from '../../../../../../store';
import * as actions from '../../../../../../store/index';
import {
  deleteFinding,
  dispatchCustomLesionModulesUpdate,
  handleClassChange,
} from '../../../DanaiView/Diagnostic/helpers/tableActions';
import { fetchPost } from '../../../../../../helpers/fetching';

const dispatchError = (dispatch, t, msg) => {
  dispatch(
    actions.setSnackbar({
      msg: msg ? `${t('diagnose.general_error')} ${msg}` : t('diagnose.general_error'),
      severity: 'error',
    })
  );
};

export const handleDeleteButtonClick = (
  studyId,
  contextMenu,
  setContextMenu,
  customMicrocalc,
  customOpacities,
  noTypeRectangles,
  dispatch,
  t
) => {
  const { rectangleType, rectangleProjection, rectangleClass, rectangleId } = contextMenu;

  if (!rectangleType) {
    const updatedNoTypeRectangles = noTypeRectangles?.[rectangleProjection]?.filter(
      (item) => item.id !== rectangleId
    );
    dispatch(updateNoTypeRectangles(rectangleProjection, updatedNoTypeRectangles));
  } else {
    const boxIndex = contextMenu?.findingIndex ?? 0;
    const lesionInfo = [rectangleProjection, rectangleClass, boxIndex];

    deleteFinding(studyId, rectangleType, lesionInfo, customMicrocalc, customOpacities, dispatch, actions, t);
  }
  setContextMenu(null);
};

const handleTypeClassChange = async (
  exam_id,
  moduleType,
  newSelectedBoxType,
  newSelectedBoxClass,
  boxInfo,
  customMicrocalc,
  customOpacities,
  dispatch,
  t
) => {
  try {
    dispatch(actions.setSavingPending(true));

    const requestBody = {
      study_id: exam_id,
      new_class: newSelectedBoxClass,
      new_type: newSelectedBoxType,
      module: moduleType,
      lesion: boxInfo,
      action: 'class_type_change',
      customMicrocalc: customMicrocalc,
      customOpacities: customOpacities,
    };

    console.log('Type and class change request body:', requestBody);

    const response = await fetchPost('diagnostic_revision_step', requestBody, 'bbox');

    console.log('Type and class change response:', response);

    if (!response?.success) {
      return dispatchError(dispatch, t, response?.msg);
    }

    const { data } = response;

    dispatchCustomLesionModulesUpdate(moduleType, data, dispatch, actions);
    dispatch(actions.updateJustEditedRows(response?.updatedFindingID));

    dispatch(
      actions.setSnackbar({
        msg: 'diagnose.type_changed',
        severity: 'success',
      })
    );
  } catch (error) {
    console.error('Error in handleTypeClassChange:', error);
    dispatchError(dispatch, t);
  } finally {
    dispatch(actions.setSavingPending(false));
  }
};

const handleNewLesion = async (
  exam_id,
  newSelectedBoxType,
  newSelectedBoxClass,
  contextMenu,
  noTypeRectangles,
  customMicrocalc,
  customOpacities,
  dispatch,
  t
) => {
  try {
    dispatch(actions.setSavingPending(true));

    const proj_name = contextMenu.rectangleProjection;
    const boxToAdd = noTypeRectangles?.[proj_name]?.find(
      (rectangle) => rectangle.id === contextMenu.rectangleId
    );

    if (!boxToAdd) {
      console.error('No rectangle found for ID:', contextMenu.rectangleId);
      dispatch(
        actions.setSnackbar({
          msg: 'diagnose.general_error',
          severity: 'error',
        })
      );
      return;
    }

    const newFindingRequestBody = {
      study_id: exam_id,
      new_finding: { box: boxToAdd.box },
      module: newSelectedBoxType,
      lesion: [contextMenu.rectangleProjection, newSelectedBoxClass],
      action: 'new',
      customMicrocalc: customMicrocalc,
      customOpacities: customOpacities,
      sop_instance_uid: contextMenu?.SOPInstanceUID,
    };
    const breast =
      contextMenu.rectangleProjection === 'rcc' || contextMenu.rectangleProjection === 'rmlo'
        ? 'right'
        : 'left';

    console.log('New lesion request body', newFindingRequestBody);

    const response = await fetchPost('diagnostic_revision_step', newFindingRequestBody, 'bbox');
    console.log('New lesion response:', response);

    if (response.success) {
      if (newSelectedBoxType === 'opacities') {
        dispatch(actions.changeOpacitiesToShow(newSelectedBoxClass, true, breast));
        dispatch(actions.setCustomOpacities(response.data.customOpacities));
        dispatch(actions.setOpacitiesLocalChanges(true));
      } else if (newSelectedBoxType === 'microcalc') {
        dispatch(actions.changeMicrocalcToShow(newSelectedBoxClass, true, breast));
        dispatch(actions.setCustomMicrocalc(response.data.customMicrocalc));
        dispatch(actions.setMicrocalcLocalChanges(true));
      }

      dispatch(actions.updateJustEditedRows(response?.updatedFindingID));

      dispatch(actions.updateNoTypeRectangles(proj_name, []));
      dispatch(
        actions.setSnackbar({
          msg: 'diagnose.new_lesion',
          severity: 'success',
        })
      );
    } else {
      dispatch(
        actions.setSnackbar({
          msg: `${t('diagnose.general_error')} ${t(`api_messages.${response.msg}`)}`,
          severity: 'error',
        })
      );
    }
  } catch (error) {
    console.error('Error creating new lesion:', error);
    dispatch(
      actions.setSnackbar({
        msg: 'diagnose.general_error',
        severity: 'error',
      })
    );
  } finally {
    dispatch(actions.setSavingPending(false));
  }
};

export const handleReassignButtonClick = async (
  exam_id,
  newSelectedBoxType,
  newSelectedBoxClass,
  contextMenu,
  resultFindings,
  customMicrocalc,
  customOpacities,
  noTypeRectangles,
  setContextMenu,
  setSelectedOption,
  dispatch,
  t
) => {
  const boxIndex = contextMenu?.findingIndex || 0;
  const proj_name = contextMenu?.rectangleProjection;
  const rectangleClass = contextMenu?.rectangleClass;
  const rectangleType = contextMenu?.rectangleType;

  // New rectangle
  if (!contextMenu.rectangleType) {
    await handleNewLesion(
      exam_id,
      newSelectedBoxType,
      newSelectedBoxClass,
      contextMenu,
      noTypeRectangles,
      customMicrocalc,
      customOpacities,
      dispatch,
      t
    );
  }

  // Microcalc rectangle
  else if (contextMenu.rectangleType === 'microcalc') {
    const prevTypeBoxesUpdated = resultFindings?.[contextMenu.rectangleProjection]?.[
      contextMenu.rectangleClass
    ].filter((_, index) => index !== boxIndex);
    if (newSelectedBoxType === 'microcalc') {
      handleClassChange(
        exam_id,
        newSelectedBoxType,
        [proj_name, rectangleClass, boxIndex],
        newSelectedBoxClass,
        customMicrocalc,
        customOpacities,
        dispatch,
        actions,
        t
      );
    } else {
      handleTypeClassChange(
        exam_id,
        rectangleType,
        newSelectedBoxType,
        newSelectedBoxClass,
        [proj_name, rectangleClass, boxIndex],
        customMicrocalc,
        customOpacities,
        dispatch,
        t
      );
    }
  }
  // Opacity rectangle
  else if (contextMenu.rectangleType === 'opacities') {
    if (newSelectedBoxType === 'opacities') {
      handleClassChange(
        exam_id,
        newSelectedBoxType,
        [proj_name, rectangleClass, boxIndex],
        newSelectedBoxClass,
        customMicrocalc,
        customOpacities,
        dispatch,
        actions,
        t
      );
    } else {
      handleTypeClassChange(
        exam_id,
        rectangleType,
        newSelectedBoxType,
        newSelectedBoxClass,
        [proj_name, rectangleClass, boxIndex],
        customMicrocalc,
        customOpacities,
        dispatch,
        t
      );
    }
  }
  setContextMenu(null);
  setSelectedOption({});
};
